import {
    CellWithTwoValues,
    GridCellEditCommitParams,
    UseFetchDataState, XGridTableWrap,
} from '@tap/mui-ui';
import { IApiAssignment, IApiIssue, IApiRelationLink, issueService } from '@tap/data-providers';
import {
    GridRowSelectionModel as IMuiGridSelectionModel,
    GridRowId,
} from '@mui/x-data-grid-pro';
import { Grid, IconButton, Button, Tooltip } from '@mui/material';
import { useCallback, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { useQCResult } from './QCResultContext';
import { ActionsCell, ActionTypeCode } from './ActionsCell';
import { Link as MuiLink } from '@mui/material';
import { Link } from 'react-router';
import { StatusFieldCell } from '../../components';

/*
*   Таблица операции
*/

type QCResultListProps = {
    issueState: UseFetchDataState<IApiIssue>,
    relationLinksState: UseFetchDataState<IApiRelationLink[]>,
    handleCreateTechIssueClick: (row: any) => void,
}

export const QCResultList = ({
    issueState,
    relationLinksState,
    handleCreateTechIssueClick,
}: QCResultListProps) => {

    const issue = issueState.data;

    const defectList = (issue.data.qcResultList || []).map((r, i) => ({ id: i, ...r }));
    const defectSum = defectList.map(d => d.amount).reduce((a, b) => a + b, 0);
    const correctRow = { id: -1, isVirtualRow: true, defectType: 'Без дефектов', defectCategory: '', amount: issue.data.amount - defectSum };

    const defaultList = [correctRow, ...defectList];

    const [isChangedToConfirm, setIsChangedToConfirm] = useState<boolean>(false);
    const [assignmentList, setAssignmentList] = useState<IApiAssignment[]>(defaultList);
    const [selectedRowIds, setSelectedRowIds] = useState<GridRowId[]>([]);
    const [changedRowIds, setChangedRowIds] = useState<GridRowId[]>([]);

    const { openAddQCResultDialog, openEditQCResultDialog } = useQCResult();

    const handleCellEditCommit = (params: GridCellEditCommitParams): void => {
        setIsChangedToConfirm(true);

        setChangedRowIds((prev) => [...new Set([...prev, params.id])]);
        setAssignmentList((prev) =>
            prev.map(row => (row.id !== params.id ? row : { ...row, [params.field]: params.value })),
        )
    }

    const handleDeleteItem = (rowId: number) => {
        setIsChangedToConfirm(true);
        setAssignmentList((prev) => (prev || []).filter(a => a.id !== rowId));
    };

    const handleChangeSelectedRows = useCallback((selectedRows: IMuiGridSelectionModel) => {
        setSelectedRowIds(selectedRows as number[]);
    }, []);

    const handleSavePlan = async () => {
        if (!issue) {
            return;
        }

        await issueService.updateIssue(issue.issueKey, {
            data: {
                ...issue?.data,
                qcResultList: assignmentList.filter(d => !d.isVirtualRow),
            },
        });

        setIsChangedToConfirm(false);
        await issueState.fetchData();

    };

    const handleCancelChanges = (): void => {
        setIsChangedToConfirm(false);
        setAssignmentList(defaultList);
    };


    const handleActionClick = (actionCode: ActionTypeCode, payload: any) => {
        switch (actionCode) {
            case ActionTypeCode.Edit:
                openEditQCResultDialog(issueState, payload.row);
                break;
            case ActionTypeCode.CreateTechIssue:
                handleCreateTechIssueClick(payload.row)
                break;
            case ActionTypeCode.Delete:
                handleDeleteItem(payload.rowId);
                break;
            default:
                break;
        }
    }



    return (
        <Grid container sx={{ position: 'relative' }}>
            <Grid
                sx={{
                    position: 'absolute',
                    top: '-35px',
                    right: '0px',
                }}
            >
                {isChangedToConfirm &&
                    <>
                        <Button
                            size='small'
                            onClick={() => handleSavePlan()}
                            startIcon={<CheckCircleOutlineOutlinedIcon />}
                        >
                            Сохранить
                        </Button>
                        <Button
                            sx={{ ml: 1 }}
                            size='small'
                            onClick={() => handleCancelChanges()}
                        >
                            Отменить
                        </Button>
                    </>
                }

                {issue.statusCode === 'work' &&
                    <>
                        <Tooltip title='Добавить'>
                            <IconButton
                                sx={{ mr: 1, ml: 2 }}
                                size='small'
                                onClick={() => openAddQCResultDialog(issueState)}
                            >
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            size='small'
                        >
                            <MoreHorizIcon />
                        </IconButton>
                    </>
                }
            </Grid>

            <Grid
                sx={{
                    height: '360px',
                    width: 1,
                    '& [role=row]:hover': {
                        '._actions': {
                            'div:first-of-type': {
                                display: 'block',
                            },
                        },
                    },
                }}
            >

                <XGridTableWrap
                    rows={assignmentList}
                    dataIsLoading={false}
                    tableHeight={'360px'}
                    tableProps={{
                        disableColumnFilter: true,
                        checkboxSelection: false,
                        onSelectionModelChange: handleChangeSelectedRows,
                        selectionModel: isChangedToConfirm ? changedRowIds : selectedRowIds,
                        slotProps: {
                            cell: {
                                tabIndex: 0,
                            },
                        },
                        slots: {
                            toolbar: null,
                        },
                        onCellEditCommit: handleCellEditCommit,
                        isRowSelectable: (params) => true,
                    }}
                    columns={
                        [
                            {
                                headerName: 'Тип брака',
                                field: 'defectType',
                                width: 340,
                                sortable: false,
                            },
                            {
                                headerName: 'Вид дефекта',
                                field: 'defectCategryName',
                                minWidth: 150,
                                flex: 1,
                                sortable: false,
                            },
                            {
                                headerName: 'Количество',
                                field: 'amount',
                                type: 'number',
                                width: 90,
                                sortable: false,
                                editable: issue.statusCode === 'work',
                            },
                            {
                                headerName: 'Технология',
                                field: 'techIssueKey',
                                minWidth: 150,
                                sortable: false,
                                renderCell: (cellParams) => {
                                    const techIssueKey = cellParams.value;
                                    const techIssue = relationLinksState.data?.find(rl => rl.relatedDocument.documentKey === techIssueKey)?.relatedDocument;

                                    return (
                                        <CellWithTwoValues
                                            primaryValue={(
                                                <MuiLink
                                                    component={Link}
                                                    to={`/${techIssueKey}`}
                                                >
                                                    {techIssueKey}
                                                </MuiLink>
                                            )}
                                            secondaryValue={(
                                                techIssue?.data.status
                                                    ? <StatusFieldCell value={techIssue?.data.status} />
                                                    : undefined
                                            )}
                                            emptySecondaryValueCharacter={''}
                                        />
                                    );
                                },
                            },
                            ...(issue.statusCode === 'work'
                                ? [{
                                    field: '_actions',
                                    width: 55,
                                    sortable: false,
                                    headerName: '',
                                    cellClassName: '_actions',
                                    renderCell: (cellParams) => <ActionsCell cellParams={cellParams}
                                        handleActionClick={handleActionClick} />,
                                }] : []),

                        ]
                    }
                />
            </Grid>
        </Grid>
    );
}



